import { emojiDefaults } from "@/components/emoji/emoji.type.ts";
import { EmojiPickerDropdown } from "@/components/emoji/EmojiPickerDropdown.tsx";
import { useTemplateFolders } from "@/queries/forms/template-folder.query.ts";
import { SurveyTemplateEditSidebar } from "@/pages/talent/surveys/templates/edit/components/SurveyTemplateEditSidebar.tsx";
import {
  TemplateEditPageProps,
  useTemplateEditPage,
} from "@/pages/talent/surveys/templates/edit/pages/survey-template-edit-pages.tsx";
import { SurveyTemplateTitleSlot } from "@/pages/talent/surveys/templates/edit/pages/SurveyTemplateTitleSlot.tsx";
import { SurveyTemplateEditPageStep } from "@/pages/talent/surveys/templates/edit/survey-template-edit.ts";
import {
  useSurveyTemplateSave,
  useSurveyTemplateMove,
} from "@/queries/surveys/use-survey-templates.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import {
  Avatar,
  Box,
  Button,
  FontAwesomeIcon,
  FullScreenPage,
  Input,
  Stack,
  Select,
} from "@skailer-com/uikit";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

export const SurveyTemplateEditDetailsPage = ({ template }: TemplateEditPageProps) => {
  const { t } = useTranslation();
  const { confirmExit, navigateToStep } = useTemplateEditPage();

  const {
    data: folders,
    isLoading: foldersLoading,
    isError: foldersError,
  } = useTemplateFolders("surveys");
  const templateSchema = z.object({
    title: z.string({ error: t("errors:field_required") }).min(1, t("errors:field_required")),
    folder_id: z
      .number({ error: t("errors:field_required") })
      .refine((id) => folders?.some((folder) => folder.id === id), t("errors:field_required")),
    emoji: z.string().min(1, t("errors:field_required")),
  });

  type TemplateSchema = z.infer<typeof templateSchema>;

  const {
    handleSubmit,
    register,
    control,
    formState: { errors },
  } = useForm<TemplateSchema>({
    mode: "onSubmit",
    resolver: zodResolver(templateSchema),
    defaultValues: {
      emoji: template.emoji || emojiDefaults.surveyTemplate.default,
      title: template.title,
      folder_id: template.folder?.id,
    },
  });

  const templateSave = useSurveyTemplateSave();
  const templateMove = useSurveyTemplateMove();
  const isSaving = templateSave.isPending || templateMove.isPending;
  const onSubmit = handleSubmit(async (data) => {
    try {
      await templateSave.mutateAsync({
        id: template.id,
        payload: { title: data.title, emoji: data.emoji },
      });

      if (data.folder_id !== template.folder?.id) {
        await templateMove.mutateAsync({ id: template.id, payload: { folder_id: data.folder_id } });
      }

      analyticsService.trackEvent(analyticEvents.surveyTemplates.edited, {
        [analyticProperties.id]: template.id,
        [analyticProperties.tab]: SurveyTemplateEditPageStep.details,
      });

      navigateToStep(template.id, SurveyTemplateEditPageStep.questions);
    } catch {
      return;
    }
  });

  return (
    <FullScreenPage
      backSlot={
        <Button variant={"secondary"} size={"lg"} onClick={confirmExit}>
          {t("common:exit")}
        </Button>
      }
      titleSlot={<SurveyTemplateTitleSlot step={SurveyTemplateEditPageStep.details} />}
      isCenterVertical={false}
      contentSize={"md"}
      sidebarSlot={
        <SurveyTemplateEditSidebar step={SurveyTemplateEditPageStep.details} template={template} />
      }
      actionsSlot={
        <Button
          onClick={onSubmit}
          size={"lg"}
          isLoading={isSaving}
          disabled={foldersLoading || foldersError}
        >
          {t("common:continue")}
        </Button>
      }
    >
      <Stack gap={"sm"} justify={"center"}>
        <Stack className={"card-panel"}>
          <Controller
            control={control}
            name={"emoji"}
            render={({ field: { value, onChange } }) => (
              <EmojiPickerDropdown onEmojiSelect={onChange}>
                <Box
                  className={
                    "relative w-41 h-41 group cursor-pointer rounded-md bg-brand-dark/5 shadow-lg"
                  }
                >
                  <Avatar variant={"plain"} url={undefined} emoji={value} size={"2xl"} />
                  <Box
                    className={
                      "bg-dark/20 opacity-0 group-hover:opacity-100 absolute inset-0 rounded-md flex items-center justify-center"
                    }
                  >
                    <FontAwesomeIcon
                      icon={"fa-light fa-icons"}
                      className={"text-[74px] text-light "}
                    />
                  </Box>
                </Box>
              </EmojiPickerDropdown>
            )}
          />
          <Input
            type={"text"}
            label={t("common:title")}
            size={"lg"}
            {...register("title")}
            error={errors.title?.message}
          />
          <Controller
            control={control}
            name="folder_id"
            render={({ field: { value, onChange } }) => (
              <Select
                label={t("core:folder")}
                emptyTitle={t("common:select")}
                options={folders}
                value={value}
                onChange={onChange}
                isLoading={foldersLoading}
                disabled={isSaving}
                required
                error={errors.folder_id?.message}
              />
            )}
          />
        </Stack>
        <Button
          onClick={onSubmit}
          size={"lg"}
          isLoading={isSaving}
          disabled={foldersLoading || foldersError}
        >
          {t("common:continue")}
        </Button>
      </Stack>
    </FullScreenPage>
  );
};
